/**
 * Comparing moments in time.
 *
 * WHY THIS MODULE EXISTS
 *   Every timestamp in Lattice is an ISO-8601 string, and it is tempting to
 *   compare two of them with `<` — sorting strings is sorting time, near
 *   enough. It is not. ISO-8601 carries an OFFSET, and this workspace genuinely
 *   mixes them: history imported from git arrives as "+05:30" from the
 *   engineers' workstations and as "Z" from the initial commit.
 *
 *     "2026-09-17T18:03:46+05:30"  is 12:33:46 UTC
 *     "2026-09-17T14:00:00Z"       is 14:00:00 UTC
 *
 *   Lexically the first is larger; in fact it is an hour and a half earlier.
 *   So a folder could report a "last modified" that is not its newest file,
 *   and a thread could sort above a newer one.
 *
 * DATA DEFINITION
 *   An Instant is an ISO-8601 string with an offset or a trailing Z.
 *   Interpretation: one moment, written in whatever timezone the machine that
 *   recorded it was in. Two Instants are only comparable through Date.parse.
 */
/** Milliseconds since the epoch, or -Infinity for anything unparseable. */
const at = (instant) => {
    const ms = Date.parse(instant);
    return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
};
/**
 * Order two Instants oldest-first. Suitable for Array.sort.
 * compareInstants : Instant Instant -> Number
 */
export const compareInstants = (a, b) => at(a) - at(b);
/** Order two Instants newest-first. byNewestInstant : Instant Instant -> Number */
export const byNewestInstant = (a, b) => at(b) - at(a);
/** The later of two Instants. laterOf : Instant Instant -> Instant */
export const laterOf = (a, b) => (at(a) >= at(b) ? a : b);
/** True when `a` is strictly later than `b`. isAfter : Instant Instant -> Boolean */
export const isAfter = (a, b) => at(a) > at(b);
//# sourceMappingURL=instant.js.map